/**
 * CandidateWelcomePage
 * Route: /candidate/welcome
 * Step 9F Green Phase
 *
 * Shows assessment instructions, comparison disclosure for Scenario 3,
 * and a "Begin Assessment" button that navigates to TP or AQ depending
 * on whether TP is skipped (Scenario 4/5).
 */

import React, { useEffect, useState } from "react";
import { PageSpinner } from "../../components/PageSpinner";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useActiveTestStore, withActiveTestId } from "../../stores/activeTestStore";
import { apiClient } from "../../services/apiClient";
import { UserRound, CheckCircle2, ClipboardX, Clock, Building2, CalendarDays, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { isClientTPScenario, SCENARIOS, type ScenarioId } from "../../constants/scenarios";
import { AssessmentInstructionsContent } from "../../components/AssessmentInstructionsContent";
import { formatLocalDateTime } from "../../lib/dateFormat";

interface VisibilityRules {
  candidate_sees_own?: boolean;
  client_manager_sees_all?: boolean;
}
interface TestInfo {
  id: string;
  name: string;
  // `description` is intentionally absent — the payload carries it, but this
  // page must not surface it to candidates. Omitting it from the type makes
  // re-adding the render a deliberate change, not a one-line slip.
  due_date?: string;
  scenario_type?: string;
  status?: string;
  client_company_name?: string | null;
  visibility_rules?: VisibilityRules;
}
interface ScenarioInfo {
  id?: string;
  name?: string;
  has_comparison?: boolean;
  skip_tp?: boolean;
}
interface AssessmentInfo {
  test?: TestInfo;
  name?: string;
  due_date?: string;
  scenario_type?: string;
  scenario?: ScenarioInfo;
  status?: string;
  is_completed?: boolean;
  progress?: { answered: number; total: number };
  tp_submitted?: boolean;
}

export function CandidateWelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const activeTestId = useActiveTestStore((s) => s.testId);

  const [assessment, setAssessment] = useState<AssessmentInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<AssessmentInfo>(withActiveTestId("/api/candidate/assessment"))
      .then((data) => {
        if (!cancelled) setAssessment(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTestId]);

  const skipTP = isClientTPScenario(assessment?.scenario);
  const hasComparison = assessment?.scenario?.has_comparison === true;
  const isCompleted = assessment?.is_completed === true || assessment?.status === "Completed";
  // Scenarios 4/5 stage the test as "Awaiting Client Input" until the
  // Client Manager submits Role TP. The candidate has nothing to do in
  // the meantime — gate Begin Assessment behind a wait screen.
  const awaitingClientTP = skipTP && assessment?.test?.status === "Awaiting Client Input";

  const answeredCount = assessment?.progress?.answered ?? 0;
  const aqStarted = answeredCount > 0;
  const tpDone = assessment?.tp_submitted === true;
  const isResume = aqStarted || tpDone;

  const handleBegin = () => {
    if (aqStarted || tpDone || skipTP) {
      navigate("/candidate/aq");
    } else {
      navigate("/candidate/tp");
    }
  };

  if (isLoading) {
    return <PageSpinner label="Loading assessment info" />;
  }

  if (error) {
    return (
      <main className="max-w-4xl mx-auto py-12 px-4">
        <div role="alert" className="p-4 bg-red-50 border border-brand-error/40 rounded-lg text-sm text-brand-error">
          {error}
        </div>
      </main>
    );
  }

  if (!assessment?.test) {
    return (
      <main className="max-w-4xl mx-auto py-12 px-4 text-center">
        <div className="w-14 h-14 rounded-lg bg-brand-panel flex items-center justify-center mb-6 mx-auto">
          <ClipboardX size={28} className="text-brand-steel" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">No assessment assigned</h1>
        <p className="text-sm text-brand-steel">
          You have not been assigned to any assessment yet. Please contact your test administrator.
        </p>
      </main>
    );
  }

  // Scenarios 4/5: Client Manager still needs to submit Role TP — show a
  // wait screen instead of "Begin Assessment" so the candidate doesn't get
  // bounced into a dead-end CTA.
  if (awaitingClientTP) {
    return (
      <main className="max-w-4xl mx-auto py-12 px-4 text-center">
        <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center mb-6 mx-auto">
          <Clock size={28} className="text-blue-600" aria-hidden="true" />
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          Please wait until your company completes the steps required before you can proceed to the next part of the
          assessment.
        </p>
      </main>
    );
  }

  // When the candidate has finished the assessment, replace the "Begin
  // Assessment" CTA with a completion acknowledgement. We don't redirect to
  // results because result visibility depends on the client manager's
  // visibility rules — the candidate may simply not have access.
  if (isCompleted) {
    return (
      <main className="max-w-4xl mx-auto py-12 px-4">
        <div className="w-14 h-14 rounded-lg bg-green-50 flex items-center justify-center mb-4 mx-auto">
          <CheckCircle2 size={28} className="text-brand-success" aria-hidden="true" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-3 text-center">Assessment Complete</h1>
        <p className="text-sm text-gray-600 leading-relaxed mb-8 text-center">
          {assessment?.test?.name
            ? `Thank you for completing the "${assessment.test.name.replace(
                /_/g,
                " "
              )}" assessment. We sincerely appreciate the time and effort you took to share your responses.`
            : "Thank you for completing the assessment. We sincerely appreciate the time and effort you took to share your responses."}
        </p>

        <div className="bg-brand-panel rounded-lg p-5 mb-8">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">What happens next</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Your results are now being securely processed. Your test administrator will reach out to share them with
            you, if applicable.
          </p>
          {/* Original visibility-rule-driven message — hidden during trial, kept here to restore alongside the "View results" button below.
          <p className="text-sm text-gray-600 leading-relaxed">
            {assessment?.test?.visibility_rules?.candidate_sees_own
              ? 'Your results are ready to view. You can also check back later if your test administrator releases additional commentary.'
              : 'Your results are being processed. Whether you receive a personal copy depends on the visibility rules set by the test administrator for this assessment. If results are made available to you, your test administrator or sponsor will share them.'}
          </p>
          */}
        </div>

        <div className="flex flex-col gap-3">
          {/* "View results" button hidden during trial — re-enable when candidate-facing results view is reintroduced.
          {assessment?.test?.visibility_rules?.candidate_sees_own && assessment.test.id && (
            <Button
              onClick={() => navigate(`/results/${assessment.test!.id}`)}
              className="w-full"
            >
              View results
            </Button>
          )}
          */}
          <Button onClick={() => navigate("/candidate/assessments")} className="w-full">
            View all assessments
          </Button>
          {/* <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="w-full"
          >
            Sign out
          </Button> */}
        </div>
      </main>
    );
  }

  // Scenario framing — short copy tailored to this test's scenario id.
  // Falls back to a generic line when the id is missing or unrecognised.
  const scenarioId = String(assessment?.scenario?.id ?? assessment?.scenario_type ?? "") as ScenarioId | "";
  const scenarioName = (scenarioId && SCENARIOS[scenarioId as ScenarioId]?.name) || assessment?.scenario?.name || null;
  const isSelfInitiated = scenarioId === "1";
  const initiatorLabel = isSelfInitiated
    ? "Self-initiated"
    : assessment?.test?.client_company_name
    ? `Invited by ${assessment.test.client_company_name}`
    : "Invited by your organization";

  return (
    <main className="max-w-4xl mx-auto py-12 px-4">
      {/* Avatar icon */}
      <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center mb-6">
        <UserRound size={28} className="text-blue-600" aria-hidden="true" />
      </div>

      {/* Heading */}
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Your Leadership Maturity Assessment</h1>
      <p className="text-sm text-gray-600 mb-6">
        A structured reflection on how you allocate attention, make decisions, and navigate real leadership situations.
        Your responses stay private and are designed for insight, not evaluation.
      </p>

      {/* Test context card — what this specific assessment is, who set it
          up, and when it's due. The generic orientation copy
          (Purpose / What to expect / How to navigate) renders below from
          the shared AssessmentInstructionsContent component, which is
          also what the top-bar "How this works" dialog wraps — one
          source of truth, two presentations.

          `test.description` is deliberately NOT rendered here. It is
          authored for the test administrator, not the candidate, so the
          client asked to keep it off the candidate-facing screen — the
          API still returns it for the admin surfaces
          (TestMonitoringPage, ClientTestDetailPage). Do not re-add. */}
      <div className="border border-brand-steel/50 bg-white rounded-lg p-5 mb-8 shadow-sm">
        {assessment?.test?.name && (
          <p className="text-base font-semibold text-gray-900 mb-3">{assessment.test.name.replace(/_/g, " ")}</p>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
          {scenarioName && (
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-brand-steel shrink-0" aria-hidden="true" />
              <dt className="sr-only">Assessment type</dt>
              <dd className="text-gray-700">{scenarioName}</dd>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-brand-steel shrink-0" aria-hidden="true" />
            <dt className="sr-only">Initiator</dt>
            <dd className="text-gray-700">{initiatorLabel}</dd>
          </div>
          {assessment?.test?.due_date && (
            <div className="flex items-center gap-2 sm:col-span-2">
              <CalendarDays size={14} className="text-brand-steel shrink-0" aria-hidden="true" />
              <dt className="sr-only">Due date</dt>
              <dd className="text-gray-700">Complete by {formatLocalDateTime(assessment.test.due_date)}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Orientation — first-time candidates see the same Purpose /
          What to expect / How to navigate content that the top-bar
          dialog wraps, so they don't have to discover the button
          before they understand what they're starting. */}
      <div className="mb-8">
        <AssessmentInstructionsContent skipTP={skipTP} hasComparison={hasComparison} />
      </div>

      {/* Resume progress hint — only when there's actual AQ progress to resume */}
      {aqStarted && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-600" aria-hidden="true" />
          <p className="text-sm text-green-800">
            You've answered <span className="font-semibold">{answeredCount}</span> question
            {answeredCount === 1 ? "" : "s"} so far. Pick up where you left off.
          </p>
        </div>
      )}

      {/* Begin / Resume button */}
      <Button
        onClick={handleBegin}
        className="w-full bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg py-2.5 transition-colors"
      >
        {isResume ? "Resume Assessment" : "Begin Assessment"}
      </Button>
    </main>
  );
}

export default CandidateWelcomePage;
